import { OverflowList } from '@theme/overflow-list';
import VariantPicker from '@theme/variant-picker';
import { Component } from '@theme/component';
import { debounce, isDesktopBreakpoint, mediaQueryLarge, yieldToMainThread } from '@theme/utilities';
import { ThemeEvents, VariantSelectedEvent, VariantUpdateEvent, SlideshowSelectEvent } from '@theme/events';
import { morph } from '@theme/morph';

export class ProductCard extends Component {
  requiredRefs = ['productCardLink'];

  get productPageUrl() {
    return this.refs.productCardLink?.href || '';
  }

  getSelectedVariantId() {
    const checkedInput = this.querySelector('input[type="radio"]:checked[data-variant-id]');
    return checkedInput?.dataset.variantId || null;
  }

  getProductCardLink() {
    return this.refs.productCardLink || null;
  }

  connectedCallback() {
    super.connectedCallback();

    if (!this.refs.productCardLink) return;

    this.#handleQuickAdd();

    this.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);
    this.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected);
    this.addEventListener(SlideshowSelectEvent.eventName, this.#handleSlideshowSelect);
    mediaQueryLarge.addEventListener('change', this.#handleQuickAdd);

    this.addEventListener('click', this.navigateToProduct);
     /* Hover image preview */
  this.addEventListener('pointerenter', this.previewImage);
  this.addEventListener('pointerleave', this.resetImage);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('click', this.navigateToProduct);
  }

  /* ---------------- Quick Add ---------------- */

  #fetchProductPageHandler = () => {
    this.refs.quickAdd?.fetchProductPage(this.productPageUrl);
  };

  #handleQuickAdd = () => {
    this.removeEventListener('pointerenter', this.#fetchProductPageHandler);
    this.removeEventListener('focusin', this.#fetchProductPageHandler);

    if (isDesktopBreakpoint()) {
      this.addEventListener('pointerenter', this.#fetchProductPageHandler);
      this.addEventListener('focusin', this.#fetchProductPageHandler);
    }
  };

  /* ---------------- Variant Handling ---------------- */

  #handleVariantSelected = (event) => {
    if (event.target !== this.variantPicker) {
      this.variantPicker?.updateSelectedOption(event.detail.resource.id);
    }
  };

  #handleVariantUpdate = (event) => {
    event.stopPropagation();

    this.updatePrice(event);
    this.#checkAvailability(event);
    this.#updateProductUrl(event);

    this.refs.quickAdd?.fetchProductPage(this.productPageUrl);

    if (event.target !== this.variantPicker) {
      this.variantPicker?.updateVariantPicker(event.detail.data.html);
    }

    this.#updateVariantImages();
  };

  updatePrice(event) {
    const priceContainer = this.querySelectorAll(`product-price [ref='priceContainer']`)[1];
    const newPriceElement = event.detail.data.html?.querySelector(`product-price [ref='priceContainer']`);

    if (priceContainer && newPriceElement) {
      morph(priceContainer, newPriceElement);
    }
  }

  #updateProductUrl(event) {
    const anchorElement = event.detail.data.html?.querySelector('product-card a');
    if (!(anchorElement instanceof HTMLAnchorElement)) return;

    const productUrl = anchorElement.href;
    if (!productUrl) return;

    this.refs.productCardLink.href = productUrl;
  }

  /* ---------------- Availability Fix ---------------- */

  #checkAvailability(event) {
    const variants = event.detail.data.html?.querySelectorAll('input[data-variant-id]');
    if (!variants) return;

    let available = false;

    variants.forEach((variant) => {
      if (variant.dataset.optionAvailable === 'true') {
        available = true;
      }
    });

    this.#toggleAddToCartButton(available);
  }

  #toggleAddToCartButton(enable) {
    const btn = this.querySelector('.add-to-cart__button button');
    if (btn instanceof HTMLButtonElement) {
      btn.disabled = !enable;
    }
  }

  /* ---------------- Images ---------------- */

  #handleSlideshowSelect = (event) => {
    const { slideshow } = this.refs;
    if (!slideshow) return;

    const selectedSlideId = event.detail?.id;
    if (selectedSlideId) {
      slideshow.select({ id: selectedSlideId }, undefined, { animate: false });
    }
  };

  #updateVariantImages() {
    const { slideshow } = this.refs;
    if (!slideshow || !this.variantPicker?.selectedOption) return;

    const selectedImageId = this.variantPicker.selectedOption.dataset.optionMediaId;
    const slides = slideshow.refs?.slides || [];

    slides.forEach((slide) => {
      if (slide.getAttribute('variant-image') == null) return;
      slide.hidden = slide.getAttribute('slide-id') !== selectedImageId;
    });

    slideshow.select({ id: selectedImageId }, undefined, { animate: false });
  }

  /* ---------------- Hover Preview ---------------- */

previewImage(event) {
  if (event.pointerType !== 'mouse') return;

  const { slideshow } = this.refs;
  if (!slideshow) return;

  slideshow.next(undefined, { animate: false });
}

resetImage(event) {
  if (event.pointerType !== 'mouse') return;

  const { slideshow } = this.refs;
  if (!slideshow) return;

  slideshow.previous(undefined, { animate: false });
}

  /* ---------------- Navigation ---------------- */

  navigateToProduct = (event) => {
    if (!(event.target instanceof Element)) return;

    const interactiveElement = event.target.closest('button, input, label, select');

    if (interactiveElement) return;

    const link = this.refs.productCardLink;
    if (!link?.href) return;

    const linkURL = new URL(link.href);

    if (event.metaKey || event.ctrlKey || event.shiftKey) {
      window.open(linkURL.href, '_blank');
    } else {
      window.location.href = linkURL.href;
    }
  };

  get variantPicker() {
    return this.querySelector('swatches-variant-picker-component');
  }
}

if (!customElements.get('product-card')) {
  customElements.define('product-card', ProductCard);
}

/* ---------------- Variant Picker ---------------- */

class SwatchesVariantPickerComponent extends VariantPicker {
  connectedCallback() {
    super.connectedCallback();
    this.parentProductCard = this.closest('product-card');

    this.addEventListener(
      ThemeEvents.variantUpdate,
      this.#handleCardVariantUrlUpdate.bind(this)
    );
  }

  #handleCardVariantUrlUpdate() {
    if (!this.pendingVariantId || !this.parentProductCard) return;

    const currentUrl = new URL(this.parentProductCard.refs.productCardLink.href);
    currentUrl.searchParams.set('variant', this.pendingVariantId);

    this.parentProductCard.refs.productCardLink.href = currentUrl.toString();
    this.pendingVariantId = null;
  }

  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    const swatch = event.target;
    const availableCount = parseInt(swatch.dataset.availableCount || '0');
    const firstAvailableVariantId = swatch.dataset.firstAvailableOrFirstVariantId;

    if (availableCount > 0 && firstAvailableVariantId) {
      event.stopPropagation();

      this.updateSelectedOption(swatch);

      const productUrl = this.dataset.productUrl?.split('?')[0];
      if (!productUrl) return;

      const url = new URL(productUrl, window.location.origin);
      url.searchParams.set('variant', firstAvailableVariantId);
      url.searchParams.set('section_id', 'section-rendering-product-card');

      this.pendingVariantId = firstAvailableVariantId;

      this.fetchUpdatedSection(url.href);
      return;
    }

    super.variantChanged(event);
  }

  showAllSwatches(event) {
    event?.preventDefault();

    const { overflowList } = this.refs;
    if (overflowList instanceof OverflowList) {
      overflowList.showAll();
    }
  }
}

if (!customElements.get('swatches-variant-picker-component')) {
  customElements.define('swatches-variant-picker-component', SwatchesVariantPickerComponent);
}